<form [formGroup]="form" class="interactive-form">
    <h4 class="form-step-heading">Prompt</h4>
    <div class="form-step">
        <div class="form-step__section">
            <p class="callout-1">Interactive Name</p>
            <p class="caption-4">
                Enter a short name for internal use (listeners won’t see this)
            </p>
            <tools-input-outline
                label="NAME"
                placeholder="Name"
                [maxLength]="constraints.name.maxLength"
                formControlName="name"
                class="interactive-form__name-input"
            ></tools-input-outline>
            @if (fieldError("name"); as msg) {
                <span class="form-error">{{ msg }}</span>
            }
        </div>

        <div class="form-step__section">
            <p class="callout-1">
                Listener Prompt
                <span
                    class="prompt-tips-trigger caption-3"
                    #origin="cdkOverlayOrigin"
                    cdkOverlayOrigin
                    (mouseenter)="showOverlay()"
                    (mouseleave)="hideOverlay()"
                >
                    View prompt tips
                </span>
                <ng-template
                    cdk-connected-overlay
                    [cdkConnectedOverlayOrigin]="origin"
                    [cdkConnectedOverlayOpen]="this.isTooltipOverlayOpen"
                    [cdkConnectedOverlayPositions]="positions"
                    [cdkConnectedOverlayPush]="true"
                >
                    <div class="tip-tooltip-panel">
                        <p class="body-1">Prompt writing tips</p>
                        <div>
                            <p class="caption-1">
                                Keep prompts under 80 characters
                            </p>
                            <ul class="caption-3">
                                <li>Prevents truncation in promo placements</li>
                                <li>Improves scanability</li>
                            </ul>
                        </div>
                        <div>
                            <p class="caption-1">
                                Shorter prompts drive more taps
                            </p>
                            <ul class="caption-3">
                                <li>Lead with the core question</li>
                                <li>Put key words first</li>
                                <li>Avoid multi-part questions</li>
                                <li>Use shorter, simpler words</li>
                            </ul>
                        </div>
                        <div>
                            <p class="caption-3">Example:</p>
                            <p>
                                <span class="caption-1">Strong:</span>
                                <span class="caption-5">
                                    Who’s winning the Best New Artist Grammy
                                    tonight?
                                </span>
                            </p>
                        </div>
                        <p>
                            <span class="caption-1">Wordy:</span>
                            <span class="caption-5">
                                Who do you think is going to take home the
                                Grammy for Best New Artist tonight, and why?
                            </span>
                        </p>
                    </div>
                </ng-template>
            </p>
            <p class="caption-4">
                Ask a clear, concise question (recommended: 80 characters max).
            </p>
            <tools-textarea
                placeholder="Enter prompt"
                [maxLength]="constraints.prompt.maxLength"
                formControlName="prompt"
            ></tools-textarea>
            @if (fieldError("prompt"); as msg) {
                <span class="form-error">{{ msg }}</span>
            }
        </div>

        <div class="form-step__section">
            <p class="callout-1">Prompt Description</p>
            <p class="caption-4">
                Add context for internal use (listeners won’t see this).
            </p>
            <tools-textarea
                class="prompt-description"
                placeholder="Enter description"
                [maxLength]="constraints.description.maxLength"
                formControlName="description"
            ></tools-textarea>
            @if (fieldError("description"); as msg) {
                <span class="form-error">{{ msg }}</span>
            }
        </div>
    </div>
    <h4 class="form-step-heading">Customization</h4>
    <div class="form-step">
        <div class="form-step__section">
            <mat-form-field class="interactive-form__tenant-select">
                <mat-label>Assigned tenant</mat-label>
                <mat-select formControlName="tenantId">
                    @for (option of tenantOptions(); track option) {
                        <mat-option [value]="option.id">
                            {{ option.name }}
                        </mat-option>
                    }
                </mat-select>
            </mat-form-field>
        </div>
        <div class="form-step__section">
            <mat-form-field class="interactive-form__type-select">
                <mat-label>Interactive type</mat-label>
                <mat-select
                    formControlName="type"
                    placeholder="&mdash; Choose interactive type &mdash;"
                >
                    @for (option of interactiveTypeOptions; track option) {
                        <mat-option [value]="option.value">
                            {{ option.label }}
                        </mat-option>
                    }
                </mat-select>
            </mat-form-field>
        </div>
        @if (form.controls.type.value === "freeform") {
            <div class="form-step__section form-step__section--response-type">
                <p class="callout-1">Response Type</p>
                <p class="caption-4">Choose one or more.</p>
                <tools-checkbox formControlName="allowsAudio">
                    Voice
                </tools-checkbox>
                @if (form.controls.allowsAudio.value) {
                    <mat-form-field class="interactive-form__duration-select">
                        <mat-label>Length</mat-label>
                        <mat-select
                            formControlName="maxDurationOverride"
                            placeholder="Choose Audio Duration"
                        >
                            @for (option of durationOptions; track option) {
                                <mat-option [value]="option.value">
                                    {{ option.label }}
                                </mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    @if (
                        fieldError(
                            "maxDurationOverride",
                            "maxDurationOverrideRequired"
                        );
                        as msg
                    ) {
                        <span class="form-error">{{ msg }}</span>
                    }
                }
                <tools-checkbox formControlName="allowsText">
                    Text
                </tools-checkbox>
                @if (form.controls.allowsText.value) {
                    <mat-form-field
                        appearance="outline"
                        class="interactive-form__max-character-input"
                    >
                        <mat-label>Max Characters</mat-label>
                        <input
                            matInput
                            type="number"
                            formControlName="maxCharacterOverride"
                            [min]="constraints.maxCharacterOverride.min"
                            [max]="constraints.maxCharacterOverride.max"
                            step="1"
                            placeholder="Enter max characters"
                        />
                    </mat-form-field>
                    @if (
                        fieldError(
                            "maxCharacterOverride",
                            "maxCharacterOverrideRequired"
                        );
                        as msg
                    ) {
                        <span class="form-error">{{ msg }}</span>
                    }
                }
                <tools-checkbox formControlName="allowsVideo">
                    Video
                </tools-checkbox>
                @if (form.controls.allowsVideo.value) {
                    <mat-form-field class="interactive-form__duration-select">
                        <mat-label>Length</mat-label>
                        <mat-select
                            formControlName="maxVideoDurationOverride"
                            placeholder="Choose Video Duration"
                        >
                            @for (
                                option of videoDurationOptions;
                                track option
                            ) {
                                <mat-option [value]="option.value">
                                    {{ option.label }}
                                </mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    @if (
                        fieldError(
                            "maxVideoDurationOverride",
                            "maxVideoDurationOverrideRequired"
                        );
                        as msg
                    ) {
                        <span class="form-error">{{ msg }}</span>
                    }
                }
                @if (
                    (form.controls.allowsAudio.touched ||
                        form.controls.allowsText.touched ||
                        form.controls.allowsVideo.touched) &&
                    form.errors?.["atLeastOneMediaType"]
                ) {
                    <span class="form-error">
                        Select at least Voice, Text, or Video.
                    </span>
                }
            </div>
        } @else {
            <div class="form-step__section">
                <div class="form-step__section-horizontal">
                    <mat-form-field>
                        <mat-label>Poll type</mat-label>
                        <mat-select formControlName="isMultiSelect">
                            @for (
                                option of pollTypeOptions;
                                track option.value
                            ) {
                                <mat-option [value]="option.value">
                                    {{ option.label }}
                                </mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    @if (form.controls.isMultiSelect.value) {
                        <mat-form-field>
                            <mat-label>Max selections allowed</mat-label>
                            <mat-select formControlName="maxSelectionsAllowed">
                                @for (
                                    option of maxSelectionsOptions;
                                    track option.value
                                ) {
                                    <mat-option [value]="option.value">
                                        {{ option.label }}
                                    </mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    } @else {
                        <mat-form-field>
                            <mat-label>
                                Display poll results to listeners
                            </mat-label>
                            <mat-select
                                formControlName="displayResultsOverridable"
                            >
                                @for (
                                    option of displayResultsOptions;
                                    track option.value
                                ) {
                                    <mat-option [value]="option.value">
                                        {{ option.label }}
                                    </mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    }
                </div>
                @if (
                    fieldError(
                        "maxSelectionsAllowed",
                        "maxSelectionsExceedsOptions"
                    );
                    as msg
                ) {
                    <span class="form-error">{{ msg }}</span>
                }
            </div>
            <div class="form-step__section">
                <p class="callout-1">Poll Options</p>
                @if (isPollOptionsEditable()) {
                    @if (form.controls.isMultiSelect.value) {
                        <p class="caption-4">
                            Add at least 2 poll selections to continue. You can
                            manually enter selections or upload a CSV file with
                            your poll selections listed in a single column, one
                            selection per row. Recommended: 35 characters max
                            per selection to prevent truncation on mobile.
                        </p>
                    } @else {
                        <p class="caption-4">
                            Add at least 2 poll selections to continue.
                            Recommended: 35 characters max per selection to
                            prevent truncation on mobile.
                        </p>
                    }
                    <div class="poll-option-controls">
                        @if (form.controls.isMultiSelect.value) {
                            <input
                                #pollOptionsCsvInput
                                type="file"
                                accept=".csv,text/csv"
                                hidden
                                (change)="onPollOptionsCsvSelected($event)"
                            />
                            <tools-button
                                [variant]="ButtonVariant.OUTLINE"
                                (onClick)="openPollOptionsCsvPicker()"
                            >
                                Import CSV
                            </tools-button>
                        }
                        @if (activePollOptionRows().length > 0) {
                            <tools-button
                                [variant]="ButtonVariant.OUTLINE"
                                (onClick)="onRemoveAllPollOptions()"
                                class="remove-selections-btn"
                            >
                                Remove All Selections
                            </tools-button>
                        }
                    </div>
                    @if (form.controls.isMultiSelect.value) {
                        <div class="poll-option-status-filters">
                            <button
                                type="button"
                                [class.is-active]="
                                    pollOptionsStatusFilter() === 'ALL'
                                "
                                (click)="setPollOptionsStatusFilter('ALL')"
                            >
                                All ({{ activePollOptionRows().length }})
                            </button>
                            <button
                                type="button"
                                [class.is-active]="
                                    pollOptionsStatusFilter() === 'EMPTY'
                                "
                                (click)="setPollOptionsStatusFilter('EMPTY')"
                            >
                                Empty ({{ pollOptionStatusCounts().EMPTY }})
                            </button>
                            <button
                                type="button"
                                [class.is-active]="
                                    pollOptionsStatusFilter() === 'LENGTHY'
                                "
                                (click)="setPollOptionsStatusFilter('LENGTHY')"
                            >
                                Lengthy ({{ pollOptionStatusCounts().LENGTHY }})
                            </button>
                            <button
                                type="button"
                                [class.is-active]="
                                    pollOptionsStatusFilter() === 'DUPLICATE'
                                "
                                (click)="
                                    setPollOptionsStatusFilter('DUPLICATE')
                                "
                            >
                                Duplicate ({{
                                    pollOptionStatusCounts().DUPLICATE
                                }})
                            </button>
                        </div>
                    }
                    @if (pollOptionCountIssueMessage(); as msg) {
                        <span class="form-error">{{ msg }}</span>
                    }
                    @if (pollOptionStatusIssueMessage(); as msg) {
                        <span class="form-error">{{ msg }}</span>
                    }
                    @if (form.controls.isMultiSelect.value) {
                        <tools-input
                            prefix="search"
                            placeholder="Search selections"
                            [formControl]="pollOptionsSearchControl"
                        />
                    }
                } @else {
                    <p class="caption-4">Total: {{ pollOptionRows.length }}</p>
                }
                <!--
                    eslint-disable-next-line @angular-eslint/template/click-events-have-key-events --
                    the only actionable descendants are native <button data-action> elements, which
                    already has their own click on Enter/Space. Adding explicit keydown
                    handlers here double-fires the delegated action (both the keydown handler and the
                    button's native click). Same reasoning applies to the Sonarqube issue (suppressed in
                    sonar-project.properties).
                -->
                <table
                    mat-table
                    [dataSource]="pagedPollOptionRows()"
                    [trackBy]="pollOptionRowTrackBy"
                    class="poll-option-table"
                    tabindex="-1"
                    (click)="onPollOptionsTableClick($event)"
                >
                    <ng-container matColumnDef="index">
                        <th mat-header-cell *matHeaderCellDef>#</th>
                        <td mat-cell *matCellDef="let row">
                            <span class="caption-3">
                                {{ pollOptionIndex(row) + 1 }}
                            </span>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="option">
                        <th mat-header-cell *matHeaderCellDef>Selection</th>
                        <td mat-cell *matCellDef="let row">
                            <span class="caption-3">{{ row.text }}</span>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="charCount">
                        <th mat-header-cell *matHeaderCellDef>Char. Count</th>
                        <td mat-cell *matCellDef="let row">
                            <span class="caption-3">
                                {{ row.text.length }}/{{
                                    constraints.pollOptions.textMaxLength
                                }}
                            </span>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="status">
                        <th mat-header-cell *matHeaderCellDef>Status</th>
                        <td mat-cell *matCellDef="let row">
                            <tools-badge
                                [variant]="
                                    pollOptionStatusBadgeLibrary[
                                        pollOptionStatus(row)
                                    ]
                                "
                            >
                                {{ pollOptionStatus(row) }}
                            </tools-badge>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef>Actions</th>
                        <td mat-cell *matCellDef="let row">
                            <button
                                type="button"
                                mat-icon-button
                                class="edit-option"
                                aria-label="Edit option"
                                data-action="edit"
                            >
                                <span class="icon-edit"></span>
                            </button>
                            <button
                                type="button"
                                mat-icon-button
                                aria-label="Add option"
                                data-action="add"
                                [disabled]="isAddPollOptionDisabled()"
                            >
                                <span class="icon-iec-add-outline"></span>
                            </button>
                            <button
                                type="button"
                                mat-icon-button
                                aria-label="Remove option"
                                data-action="remove"
                                [disabled]="isRemovePollOptionDisabled()"
                            >
                                <span class="icon-trash-outline"></span>
                            </button>
                        </td>
                    </ng-container>

                    <tr
                        mat-header-row
                        *matHeaderRowDef="pollOptionsActiveTableColumns()"
                    ></tr>
                    <tr
                        mat-row
                        *matRowDef="
                            let row;
                            columns: pollOptionsActiveTableColumns();
                            let i = index
                        "
                        [attr.data-row-index]="i"
                    ></tr>
                </table>
                @if (filteredPollOptionRows().length === 0) {
                    <div class="poll-option-empty-state">
                        No selections match this filter.
                    </div>
                }
                <div class="poll-option-table-paginator">
                    <tools-paginator
                        [length]="filteredPollOptionRows().length"
                        [pageSize]="pollOptionsPageSize()"
                        [pageIndex]="clampedPollOptionsPageIndex()"
                        [pageSizeOptions]="pollOptionsPageSizeOptions"
                        (page)="onPollOptionsPageChange($event)"
                    />
                    <mat-form-field appearance="outline">
                        <mat-label>Rows per page</mat-label>
                        <mat-select
                            [value]="pollOptionsPageSize()"
                            (selectionChange)="
                                onPollOptionsPageSizeChange($event.value)
                            "
                        >
                            @for (
                                size of pollOptionsPageSizeOptions;
                                track size
                            ) {
                                <mat-option [value]="size">
                                    {{ size }}
                                </mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </div>
            </div>
        }
    </div>
    <h4 class="form-step-heading">Schedule</h4>
    <div class="form-step">
        <div class="form-step__section-horizontal">
            <div>
                <p class="callout-1">Choose Start Date</p>
                <mat-radio-group
                    formControlName="hasStartDate"
                    aria-label="Select a start date"
                >
                    <mat-radio-button [value]="false">
                        Start immediately
                    </mat-radio-button>
                    <mat-radio-button [value]="true">
                        Set start date and time
                    </mat-radio-button>
                </mat-radio-group>
                @if (form.controls.hasStartDate.value) {
                    <app-date-picker
                        formControlName="startDate"
                    ></app-date-picker>
                    @if (
                        fieldError(
                            "startDate",
                            "startDateRequired",
                            "startDateInPast"
                        );
                        as msg
                    ) {
                        <span class="form-error">{{ msg }}</span>
                    }
                }
            </div>
            <div>
                <p class="callout-1">Choose End Date</p>
                <mat-radio-group
                    formControlName="hasEndDate"
                    aria-label="Select a start date"
                >
                    <mat-radio-button
                        [value]="false"
                        [disabled]="isSingleSelectPoll()"
                    >
                        No end date (runs until manually ended)
                    </mat-radio-button>
                    <mat-radio-button [value]="true">
                        Set end date and time
                    </mat-radio-button>
                </mat-radio-group>
                @if (form.controls.hasEndDate.value || isSingleSelectPoll()) {
                    <app-date-picker
                        formControlName="endDate"
                    ></app-date-picker>
                    @if (
                        fieldError(
                            "endDate",
                            "endDateRequired",
                            "endDateBeforeStartDate",
                            "endDateInPast"
                        );
                        as msg
                    ) {
                        <span class="form-error">{{ msg }}</span>
                    }
                }
            </div>
        </div>
    </div>

    <div class="action-bar">
        @if (availableTargetStates().includes("DRAFT")) {
            <tools-button
                [variant]="ButtonVariant.OUTLINE"
                [isDisabled]="isSaveDraftDisabled()"
                (onClick)="onSaveDraft()"
            >
                Save as Draft
            </tools-button>
        }
        @if (availableTargetStates().includes("PAUSED")) {
            <tools-button
                [variant]="ButtonVariant.OUTLINE"
                [isDisabled]="isSubmitDisabled()"
                (onClick)="onSavePaused()"
            >
                Save
            </tools-button>
        }
        @if (availableTargetStates().includes("PUBLISHED")) {
            <tools-button
                [isDisabled]="isSubmitDisabled()"
                (onClick)="onPublish()"
            >
                {{
                    !currentState() ||
                    currentState() === "DRAFT" ||
                    availableTargetStates().includes("PAUSED")
                        ? "Publish Interactive"
                        : "Save"
                }}
            </tools-button>
        }
    </div>
</form>
